import * as Sentry from '@sentry/react';
import { updateSentryUserContext } from 'helpers/sentryUserContext';

const isProd = import.meta.env.REACT_APP_ENVIRONMENT === 'prod';

const API_URL = import.meta.env.REACT_APP_HOSTNAME;

if (isProd) {
  Sentry.init({
    dsn: 'https://4056cb371064f8e230f9c64e58400080@o4510388314243072.ingest.us.sentry.io/4510388381614080',
    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        networkDetailAllowUrls: [
          API_URL,
          'https://cognito-idp.us-west-2.amazonaws.com/',
          'https://api.stripe.com/',
        ],
        networkCaptureBodies: true,
        // backend trace id for log correlation
        networkResponseHeaders: ['X-Trace-Id'],
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
        failedRequestTargets: [API_URL],
      }),
    ],
    // Session Replay
    replaysSessionSampleRate: 0, // Records 0% of all user sessions, even if no error happens.
    replaysOnErrorSampleRate: 1.0, // Records 100% of sessions where an error occurs,

    beforeSend(event, hint) {
      const error = hint?.originalException as Error | undefined;

      const message =
        error?.message ||
        event.message ||
        event.exception?.values?.[0]?.value ||
        '';

      // Ignore ChunkLoadError, CSS loading errors and 3rd party scripts noise
      if (
        message.includes('Unable to preload CSS') ||
        message.includes('Loading CSS chunk') ||
        message.includes('ChunkLoadError') ||
        message.includes('Importing a module script failed') ||
        message.includes('is not a valid JavaScript MIME type') ||
        message.match(/\.css($|\?)/) ||
        message.match(/\.js($|\?)/) ||
        message.includes('ResizeObserver loop limit exceeded') ||
        message.includes('Script error.') ||
        message.includes('Non-Error promise rejection') ||
        message.includes('WKWebView API client did not respond') ||
        message.includes("reading 'postMessage'") ||
        message.includes('postMessage') || 
        message.includes('Converting circular structure to JSON')
      ) {
        return null;
      }

      // Group and tag HTTP client errors by endpoint
      if (event.contexts?.response) {
        event.tags = { ...event.tags, 'error.origin': 'backend' };

        const method = event.request?.method || 'UNKNOWN';
        const url = event.request?.url || '';
        // Normalize URL: strip IDs (UUIDs, numeric) so same endpoint groups together
        const path = new URL(url, 'https://placeholder').pathname
          .replace(
            /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
            '/:id',
          )
          .replace(/\/\d+/g, '/:id');
        const statusCode = event.contexts.response.status_code;

        event.fingerprint = [
          'http-client-error',
          method,
          path,
          String(statusCode),
        ];
      }

      return event;
    },
  });

  // Initialize Sentry user context from localStorage if available
  updateSentryUserContext();
}
